/* Geral / Bibliotecas */
import React, { useState } from 'react'
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import Toggle from 'react-toggle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ReactTable from "react-table"
import "react-toggle/style.css"
import "react-table/react-table.css"

/* Métodos / Ações */
import { fetchList as fetchDocumentosList } from '../../actions/documentos'
import { fetchDocumentosFuncionariosEmpresaList, saveDocumentosFuncionariosEmpresa } from '../../actions/empresas';

/* Botões */
import ButtonSuccess from '../Buttons/ButtonSuccess'

/* Cores */
import { COLOR_DARK2 } from '../../Colors'
import { useEffect } from 'react';

const MySwal = withReactContent(Swal)

const DocumentosEmpresaModal = ({ dispatch, documentos = [], loadingDocumentos, onAfterSave }) => {
  const [selected, setSelected] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);

  const isSelectedAll = documentos.length === selected.size;

  const handleSubmit = async () => {
    if (loadingDocumentos || isLoading) return;

    setIsLoading(true);
    try {
      await saveDocumentosFuncionariosEmpresa({ documentos: [...selected] });

      MySwal.fire({
        title: "Documentos salvos com sucesso",
        toast: true,
        type: "success",
        timer: 5000,
        position: "bottom-end",
        showConfirmButton: false,
        showCancelButton: false,
      });

      if (typeof onAfterSave === 'function') onAfterSave();
    } catch {
      MySwal.fire({
        title: "Falha ao salvar documentos",
        toast: true,
        type: "error",
        timer: 5000,
        position: "bottom-end",
        showConfirmButton: false,
        showCancelButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = ({ idDocumento }) => {
    setSelected(
      state => state.has(idDocumento)
        ? new Set([...state].filter(id => id !== idDocumento))
        : new Set([...state, idDocumento])
    );
  };

  const handleToggleSelectAll = () => {
    setSelected(
      new Set(
        isSelectedAll
          ? []
          : documentos?.map((documento) => documento.idDocumento)
      )
    )
  };

  useEffect(() => {
    dispatch(fetchDocumentosList({ tipo: 'FUN' }));
  }, [dispatch])

  useEffect(() => {
    setIsLoading(true);

    try {
      (async () => {
        const { data } = await fetchDocumentosFuncionariosEmpresaList();
        setSelected(new Set(data));
      })()
    } catch {
      MySwal.fire({
        title: "Erro ao carregar os documentos selecionados",
        toast: true,
        type: "error",
        timer: 5000,
        position: "bottom-end",
        showConfirmButton: false,
        showCancelButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  }, [])

  return (
    <div className="pb-5">
      <div className="row mx-0 d-flex align-items-center justify-content-center">
        <div className="col-12 px-4">
          <div className="card">
            <div className="card-body px-2">
              <h5 className="font-weight-500 mb-3" style={{ color: COLOR_DARK2 }}>
                <FontAwesomeIcon icon="signature" /> Documentos
              </h5>

              <div className="row">
                <div className="col-12">
                  <button type="button" onClick={handleToggleSelectAll} className="small position-absolute" style={{
                    right: '24px',
                    top: '8px',
                    zIndex: '999',
                    color: COLOR_DARK2
                  }}>
                    {isSelectedAll ? "Remover" : "Selecionar"} todos
                  </button>
                  <ReactTable
                    columns={[
                      {
                        Header: "Nome",
                        accessor: "nome",
                        headerClassName: 'nome-placeholder documentos-filtro',
                        className: "font-weight-600 text-uppercase",
                        filterable: true,
                        Cell: (props) => <div className="d-flex align-items-center">
                          <Toggle
                            checked={selected.has(props.original.idDocumento)}
                            className="mr-3"
                            onChange={() => handleChange(props.original)}
                          />
                          {' ' + props.original.nome}
                        </div>,
                      },
                    ]}
                    multiSort={false}
                    data={documentos}
                    loading={loadingDocumentos || isLoading}
                    previousText={<FontAwesomeIcon icon="chevron-left" />}
                    nextText={<FontAwesomeIcon icon="chevron-right" />}
                    resizable={false}
                    loadingText="Carregando..."
                    noDataText="Nenhum documento encontrado"
                    pageText="Página"
                    ofText="de"
                    rowsText="linhas"
                    pageJumpText="ir para a página"
                    rowsSelectorText="linhas por página"
                    pageSizeOptions={[5, 10, 20, 50]}
                    defaultPageSize={10}
                    minRows={0}
                    sortable={false}
                    className="-highlight"
                    defaultFilterMethod={(filter, row, column) => {
                      const id = filter.pivotId || filter.id
                      return row[id] !== undefined ? String(row[id]).toUpperCase().startsWith(filter.value.toUpperCase()) : true
                    }}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-12 d-flex align-items-center justify-content-center pt-4">
                  <ButtonSuccess onClick={handleSubmit} type="submit" disabled={loadingDocumentos || isLoading}>
                    <FontAwesomeIcon icon="save" /> Salvar
                  </ButtonSuccess>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

DocumentosEmpresaModal.propTypes = {
  documentos: PropTypes.arrayOf(PropTypes.shape({
    idDocumento: PropTypes.number.isRequired,
    nome: PropTypes.string.isRequired,
    validade: PropTypes.number.isRequired,
    letra: PropTypes.string,
    letra_len: PropTypes.number,
    status: PropTypes.number,
    recusados: PropTypes.number,
    aprovados: PropTypes.number,
    pendentes: PropTypes.number,
    expirando: PropTypes.number,
    comentarios: PropTypes.number,
    comentarios_lidos: PropTypes.number,
    comentarios_nao_lidos: PropTypes.number,
    dataInicio: PropTypes.any,
    descricao: PropTypes.string,
  })),
  loadingDocumentos: PropTypes.bool,
  onAfterSave: PropTypes.func,
}

const mapStateToProps = (state) => ({
  documentos: state.documentos.documentos,
  loadingDocumentos: state.documentos.loading,
});

export default connect(mapStateToProps)(DocumentosEmpresaModal);
